import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Command from "@/pages/Command";
import Markets from "@/pages/Markets";
import Desks from "@/pages/Desks";
import Trades from "@/pages/Trades";
import Backtest from "@/pages/Backtest";
import Analytics from "@/pages/Analytics";
import NewsCalendar from "@/pages/NewsCalendar";
import Broker from "@/pages/Broker";
import Risk from "@/pages/Risk";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Intelligence from "@/pages/Intelligence";
import Sources from "@/pages/Sources";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center font-mono text-xs text-muted-foreground" data-testid="auth-loading">AUTHENTICATING…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
}

function Perm({ p, children }) {
  const { can } = useAuth();
  return can(p) ? children : <div className="text-sm text-muted-foreground" data-testid="no-permission">You do not have permission to view this page.</div>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route element={<Protected />}>
            <Route index element={<Perm p="view_dashboard"><Dashboard /></Perm>} />
            <Route path="command" element={<Perm p="manage_users"><Command /></Perm>} />
            <Route path="markets" element={<Perm p="view_markets"><Markets /></Perm>} />
            <Route path="desks" element={<Perm p="run_analysis"><Desks /></Perm>} />
            <Route path="trades" element={<Perm p="view_dashboard"><Trades /></Perm>} />
            <Route path="backtest" element={<Perm p="backtest"><Backtest /></Perm>} />
            <Route path="analytics" element={<Perm p="view_analytics"><Analytics /></Perm>} />
            <Route path="news" element={<Perm p="view_markets"><NewsCalendar /></Perm>} />
            <Route path="intelligence" element={<Perm p="view_markets"><Intelligence /></Perm>} />
            <Route path="sources" element={<Perm p="view_markets"><Sources /></Perm>} />
            <Route path="broker" element={<Perm p="manage_brokers"><Broker /></Perm>} />
            <Route path="risk" element={<Perm p="view_dashboard"><Risk /></Perm>} />
            <Route path="settings" element={<Perm p="manage_credentials"><Settings /></Perm>} />
            <Route path="users" element={<Perm p="manage_users"><Users /></Perm>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
    </AuthProvider>
  );
}
